// =============================================================================
// AGENT 1 — ARCHITEKT REPOZYTORIUM: GLOBALNY STORE STANU GRY
// Eksponuje Context API + hook useGame() dla wszystkich komponentów.
// Nie zawiera logiki scoringu (to domena Agent 3: src/engine/).
// =============================================================================

import {
  createContext,
  useContext,
  useReducer,
  useCallback,
  type ReactNode,
} from 'react';
import type {
  GameState,
  Assignment,
  ScoreResult,
  FinishMeta,
  FinishReason,
} from '../types/models';
import { GAME_DURATION_SECONDS } from './constants';

// ---------------------------------------------------------------------------
// Stan początkowy
// ---------------------------------------------------------------------------

const initialState: GameState = {
  phase: 'idle',
  timeLeft: GAME_DURATION_SECONDS,
  assignments: [],
  finishReason: null,
  finishMeta: {
    ignoredAwariaByTaskId: {},
    excessTaskByTaskId: {},
  },
  score: null,
};

// ---------------------------------------------------------------------------
// Akcje
// ---------------------------------------------------------------------------

type GameAction =
  | { type: 'START_GAME' }
  | { type: 'TICK' }
  | { type: 'ASSIGN_TASK'; payload: Assignment }
  | { type: 'UNASSIGN_TASK'; payload: { taskId: string } }
  | { type: 'REQUEST_FINISH'; payload: { reason: FinishReason; meta?: Partial<FinishMeta> } }
  | { type: 'END_GAME'; payload: ScoreResult }
  | { type: 'RESET_GAME' };

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------

function gameReducer(state: GameState, action: GameAction): GameState {
  switch (action.type) {
    case 'START_GAME':
      return { ...initialState, phase: 'playing' };

    case 'TICK': {
      const next = state.timeLeft - 1;
      if (next <= 0) return { ...state, timeLeft: 0 };
      return { ...state, timeLeft: next };
    }

    case 'ASSIGN_TASK': {
      const filtered = state.assignments.filter(
        (a) => a.taskId !== action.payload.taskId,
      );
      if (state.phase === 'finished') {
        return state;
      }
      return { ...state, assignments: [...filtered, action.payload] };
    }

    case 'UNASSIGN_TASK':
      return {
        ...state,
        assignments: state.assignments.filter(
          (a) => a.taskId !== action.payload.taskId,
        ),
      };

    case 'REQUEST_FINISH': {
      if (state.phase !== 'playing') {
        return state;
      }

      return {
        ...state,
        phase: 'finishing',
        finishReason: action.payload.reason,
        finishMeta: {
          ignoredAwariaByTaskId: {
            ...state.finishMeta.ignoredAwariaByTaskId,
            ...(action.payload.meta?.ignoredAwariaByTaskId ?? {}),
          },
          excessTaskByTaskId: {
            ...state.finishMeta.excessTaskByTaskId,
            ...(action.payload.meta?.excessTaskByTaskId ?? {}),
          },
        },
      };
    }

    case 'END_GAME':
      if (state.phase !== 'finishing') {
        return state;
      }

      return { ...state, phase: 'finished', score: action.payload };

    case 'RESET_GAME':
      return initialState;

    default:
      return state;
  }
}

export const __internal = {
  gameReducer,
  initialState,
};

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

interface GameContextValue {
  state: GameState;
  startGame: () => void;
  tick: () => void;
  assignTaskToWorker: (taskId: string, workerId: string) => void;
  unassignTask: (taskId: string) => void;
  requestFinish: (reason: FinishReason, meta?: Partial<FinishMeta>) => void;
  endGame: (result: ScoreResult) => void;
  resetGame: () => void;
  getWorkerTaskCount: (workerId: string) => number;
}

const GameContext = createContext<GameContextValue | null>(null);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function GameProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(gameReducer, initialState);

  const startGame = useCallback(() => dispatch({ type: 'START_GAME' }), []);
  const tick = useCallback(() => dispatch({ type: 'TICK' }), []);
  const assignTaskToWorker = useCallback(
    (taskId: string, workerId: string) =>
      dispatch({
        type: 'ASSIGN_TASK',
        payload: { taskId, workerId, assignedAt: Date.now() },
      }),
    [],
  );
  const unassignTask = useCallback(
    (taskId: string) => dispatch({ type: 'UNASSIGN_TASK', payload: { taskId } }),
    [],
  );
  const requestFinish = useCallback(
    (reason: FinishReason, meta?: Partial<FinishMeta>) =>
      dispatch({
        type: 'REQUEST_FINISH',
        payload: { reason, meta },
      }),
    [],
  );
  const endGame = useCallback(
    (result: ScoreResult) => dispatch({ type: 'END_GAME', payload: result }),
    [],
  );
  const resetGame = useCallback(() => dispatch({ type: 'RESET_GAME' }), []);

  // Helper: Count tasks assigned to a worker
  const getWorkerTaskCount = useCallback(
    (workerId: string) =>
      state.assignments.filter((a) => a.workerId === workerId).length,
    [state.assignments],
  );

  return (
    <GameContext.Provider
      value={{
        state,
        startGame,
        tick,
        assignTaskToWorker,
        unassignTask,
        requestFinish,
        endGame,
        resetGame,
        getWorkerTaskCount,
      }}
    >
      {children}
    </GameContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useGame(): GameContextValue {
  const ctx = useContext(GameContext);
  if (!ctx) throw new Error('useGame must be used within <GameProvider>');
  return ctx;
}
